import { useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import { GradientCircle } from '../../components/GradientCircle';
import { ShareButton } from '../../components/ShareButton';
import { colors, sectionTitleGradient } from '../../constants';

export interface SponsorArticle {
  sponsor: string;
  title: string;
  author: {
    name: string;
    role: string;
    image?: string;
    link?: string;
  };
  content: string[];
  references?: string[];
}

interface SponsorArticlesProps {
  id: string;
  articles: SponsorArticle[];
}

const ARTICLES_PER_PAGE = 4;

function AuthorAvatar({
  name,
  image,
  link,
  size = 32,
}: {
  name: string;
  image?: string;
  link?: string;
  size?: number;
}) {
  const handleNavigate = () => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  return (
    <Box
      component={Avatar}
      src={image}
      alt={name}
      onClick={handleNavigate}
      sx={{
        width: size,
        height: size,
        cursor: 'pointer',
        backgroundColor: 'primary.main',
      }}>
      <Typography
        color="white"
        fontWeight={700}
        lineHeight={1}
        sx={{ fontSize: size * 0.5 }}>
        {name.charAt(0).toUpperCase()}
      </Typography>
    </Box>
  );
}

function ArticleDialog({
  open,
  article,
  onClose,
}: {
  open: boolean;
  article: SponsorArticle | null;
  onClose: () => void;
}) {
  if (!article) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: colors.bgSection,
          backgroundImage: 'none',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '82vh', md: '88vh' },
          maxWidth: 'md',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          pb: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontSize="0.65rem"
            fontWeight={700}
            color="primary.main"
            letterSpacing={1.5}
            textTransform="uppercase"
            mb={1}>
            {article.sponsor}
          </Typography>
          <Typography
            variant="h6"
            color="white"
            fontWeight={700}
            lineHeight={1.3}>
            {article.title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="fechar artigo"
          size="small"
          sx={{ color: colors.text, flexShrink: 0, mt: 0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ borderColor: 'rgba(255,255,255,0.08)', py: 3, flex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {article.content.map((paragraph, idx) => (
            <Typography
              key={idx}
              variant="body2"
              color={colors.text}
              lineHeight={1.85}>
              {paragraph}
            </Typography>
          ))}

          {article.references && article.references.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
              <Typography
                fontSize="0.65rem"
                color={colors.textDim}
                fontWeight={700}
                mb={1.5}
                textTransform="uppercase"
                letterSpacing={1.5}>
                Referências
              </Typography>
              {article.references.map((ref, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  color={colors.textDim}
                  fontSize="0.78rem"
                  mb={0.75}
                  lineHeight={1.6}>
                  [{idx + 1}] {ref}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'rgba(255,255,255,0.08)',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AuthorAvatar
            name={`${article.author.name} | ${article.author.role}`}
            image={article.author.image}
            link={article.author.link}
            size={36}
          />
          <Box>
            <Typography
              fontSize="0.6rem"
              color={colors.textDim}
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={1}>
              Artigo do Patrocinador
            </Typography>
            <Typography fontSize="0.85rem" color="white" fontWeight={600}>
              {`${article.author.name} | ${article.author.role}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export function SponsorArticles({ id, articles }: SponsorArticlesProps) {
  const { market } = useParams<YearbookParams>();
  const [page, setPage] = useState(0);
  const [localSelected, setLocalSelected] = useState(0);
  const [displayedSelected, setDisplayedSelected] = useState(0);
  const [isFeaturedVisible, setIsFeaturedVisible] = useState(true);
  const [dialogArticle, setDialogArticle] = useState<SponsorArticle | null>(
    null,
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const totalPages = Math.ceil(articles.length / ARTICLES_PER_PAGE);
  const pageStart = page * ARTICLES_PER_PAGE;
  const pageArticles = articles.slice(pageStart, pageStart + ARTICLES_PER_PAGE);
  const safeSelected =
    displayedSelected < pageArticles.length ? displayedSelected : 0;
  const featured = pageArticles[safeSelected];

  const listItems = pageArticles.map((article, originalIdx) => ({
    article,
    originalIdx,
  }));

  useEffect(() => {
    if (localSelected === displayedSelected) {
      return;
    }

    setIsFeaturedVisible(false);

    const timeout = window.setTimeout(() => {
      setDisplayedSelected(localSelected);
      setIsFeaturedVisible(true);
    }, 180);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [displayedSelected, localSelected]);

  useEffect(() => {
    if (pageArticles.length <= 1 || dialogArticle) {
      return;
    }

    const interval = window.setInterval(() => {
      setLocalSelected((prev) => (prev + 1) % pageArticles.length);
    }, 4500);

    return () => {
      window.clearInterval(interval);
    };
  }, [dialogArticle, pageArticles.length]);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    setLocalSelected(0);
    setDisplayedSelected(0);
    setIsFeaturedVisible(true);
  }

  if (articles.length === 0) return null;

  return (
    <Box
      component="section"
      id={id}
      display="flex"
      flexDirection="column"
      gap={4}
      mt={10}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center',
        }}>
        <Typography
          component="h2"
          fontSize={{ xs: 28, md: 40 }}
          fontWeight={700}
          lineHeight={1.2}
          sx={{
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundImage: sectionTitleGradient,
          }}>
          Artigos & Análises
        </Typography>
        <ShareButton market={market} section={kebabcase('Artigos')} />
        <GradientCircle left="0" size={isMobile ? 300 : 500} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
        }}>
        <Box
          onClick={() => setDialogArticle(featured)}
          sx={{
            position: 'relative',
            flex: { xs: 'none', md: '0 0 55%' },
            minWidth: 0,
            bgcolor: colors.bgSection,
            borderRadius: 3,
            overflow: 'hidden',
            minHeight: { xs: 280, md: 380 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            opacity: isFeaturedVisible ? 1 : 0,
            transform: isFeaturedVisible ? 'translateY(0)' : 'translateY(10px)',
            '&:hover': { opacity: 0.88 },
          }}>
          <Typography
            aria-hidden
            sx={{
              position: 'absolute',
              right: -24,
              top: -24,
              fontSize: '220px',
              fontWeight: 900,
              color: 'white',
              opacity: 0.035,
              lineHeight: 1,
              userSelect: 'none',
              pointerEvents: 'none',
              zIndex: 1,
            }}>
            {featured.sponsor.charAt(0)}
          </Typography>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.93) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.18) 100%)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 3,
              p: { xs: 2.5, md: 3.5 },
            }}>
            <Box
              sx={{
                display: 'inline-flex',
                bgcolor: 'primary.main',
                borderRadius: 0.75,
                px: 1.5,
                py: 0.5,
                mb: 2,
              }}>
              <Typography
                fontSize="0.62rem"
                fontWeight={800}
                color="white"
                letterSpacing={1.5}
                textTransform="uppercase">
                Destaque
              </Typography>
            </Box>

            <Typography
              component="h3"
              fontSize={{ xs: '1.05rem', md: '1.3rem' }}
              fontWeight={700}
              color="white"
              lineHeight={1.35}
              mb={2.5}>
              {featured.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AuthorAvatar
                name={featured.author.name}
                image={featured.author.image}
                size={32}
              />
              <Typography
                fontSize="0.68rem"
                color={colors.text}
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.8}>
                Por {featured.author.name} | {featured.author.role}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}>
          {listItems.map(({ article, originalIdx }, listIdx) => {
            const isActive = originalIdx === safeSelected;

            return (
              <Box key={originalIdx} sx={{ flex: 1 }}>
                <Box
                  onClick={() => {
                    setLocalSelected(originalIdx);
                    setDialogArticle(article);
                  }}
                  sx={{
                    py: { xs: 2, md: 2.5 },
                    px: 1.5,
                    mx: -1.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    transition: 'all 0.15s ease-in-out',
                    bgcolor: isActive
                      ? 'rgba(255,255,255,0.04)'
                      : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.04)',
                    },
                  }}>
                  <Typography
                    fontSize="0.6rem"
                    color="primary.main"
                    fontWeight={700}
                    textTransform="uppercase"
                    letterSpacing={1.8}
                    mb={0.75}>
                    {article.sponsor}
                  </Typography>

                  <Typography
                    color="white"
                    fontWeight={700}
                    fontSize="0.92rem"
                    lineHeight={1.45}
                    mb={1.5}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                    {article.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AuthorAvatar
                      name={`${article.author.name} | ${article.author.role}`}
                      image={article.author.image}
                      size={26}
                    />
                    <Typography
                      fontSize="0.6rem"
                      color={colors.textDim}
                      fontWeight={600}
                      textTransform="uppercase"
                      letterSpacing={0.8}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {`${article.author.name} | ${article.author.role}`}
                    </Typography>
                  </Box>
                </Box>

                {listIdx < listItems.length - 1 && (
                  <Divider
                    sx={{ borderColor: 'rgba(255,255,255,0.07)', mx: 0 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            pt: 1,
          }}>
          <IconButton
            size="small"
            aria-label="página anterior"
            disabled={page === 0}
            onClick={() => handlePageChange(page - 1)}
            sx={{
              width: 32,
              height: 32,
              bgcolor: page === 0 ? 'rgba(255,255,255,0.06)' : 'primary.main',
              color: page === 0 ? 'rgba(255,255,255,0.25)' : 'white',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: page === 0 ? 'rgba(255,255,255,0.06)' : 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.25)',
              },
            }}>
            <ChevronLeft fontSize="small" />
          </IconButton>

          {Array.from({ length: totalPages }).map((_, i) => (
            <Box
              key={i}
              role="button"
              tabIndex={0}
              aria-label={`ir para página ${i + 1}`}
              onClick={() => handlePageChange(i)}
              onKeyDown={(e) => e.key === 'Enter' && handlePageChange(i)}
              sx={{
                height: 8,
                width: i === page ? 24 : 8,
                borderRadius: 1,
                bgcolor: i === page ? 'primary.main' : 'rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  bgcolor:
                    i === page ? 'primary.main' : 'rgba(255,255,255,0.38)',
                },
              }}
            />
          ))}

          <IconButton
            size="small"
            aria-label="próxima página"
            disabled={page === totalPages - 1}
            onClick={() => handlePageChange(page + 1)}
            sx={{
              width: 32,
              height: 32,
              bgcolor:
                page === totalPages - 1
                  ? 'rgba(255,255,255,0.06)'
                  : 'primary.main',
              color:
                page === totalPages - 1 ? 'rgba(255,255,255,0.25)' : 'white',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor:
                  page === totalPages - 1
                    ? 'rgba(255,255,255,0.06)'
                    : 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.25)',
              },
            }}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      )}

      <ArticleDialog
        open={!!dialogArticle}
        article={dialogArticle}
        onClose={() => setDialogArticle(null)}
      />
    </Box>
  );
}
